import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { userFacingError } from '../i18n/userFacingError';
import { registerWebPush } from '../push/webPush';
import { useTenant } from './TenantContext';
import { userBelongsToTenant } from '../utils/tenantProfile';

function userFromAccessToken(token) {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1] || ''));
    if (!payload?.sub && !payload?.username) return null;
    return {
      id: payload.sub,
      username: payload.username,
      email: payload.email,
      organization_id: payload.organization_id,
      roles: payload.roles || [],
      company_id: payload.company_id,
      person_id: payload.person_id,
      enterprise_id: payload.enterprise_id,
    };
  } catch {
    return null;
  }
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { tenant } = useTenant();
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar se há token válido ao carregar a aplicação
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setBooting(false);
      return;
    }

    const clearSession = () => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
    };

    const loadProfile = async (attempt = 1) => {
      try {
        const response = await authService.getProfile();
        const profile = response.data;
        if (!userBelongsToTenant(profile, tenant)) {
          clearSession();
          return;
        }
        setUser(profile);
        setIsAuthenticated(true);
        registerWebPush();
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          clearSession();
          return;
        }
        if (attempt < 3) {
          await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          return loadProfile(attempt + 1);
        }
        const fallback = userFromAccessToken(token);
        if (fallback && userBelongsToTenant(fallback, tenant)) {
          setUser(fallback);
          setIsAuthenticated(true);
        }
      }
    };

    loadProfile().finally(() => setBooting(false));
  }, []);

  const applySession = (payload) => {
    const { access_token, refresh_token, user: userData } = payload || {};
    if (!access_token || !userData) {
      const error = i18n.t('loginError', { ns: 'auth' });
      toast.error(error);
      return { success: false, error };
    }
    if (!userBelongsToTenant(userData, tenant)) {
      const error = i18n.t('tenantLoadError', { ns: 'auth' });
      toast.error(error);
      return { success: false, error };
    }
    localStorage.setItem('authToken', access_token);
    if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
    setUser(userData);
    setIsAuthenticated(true);
    registerWebPush();
    toast.success(i18n.t('loginSuccess', { ns: 'auth' }));
    return { success: true, user: userData };
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      if (response.data?.requires_2fa && response.data?.temp_token) {
        return { success: false, requires_2fa: true, temp_token: response.data.temp_token };
      }
      return applySession(response.data);
    } catch (error) {
      const errorMessage = userFacingError(error, 'auth:loginError');
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const login2fa = async (tempToken, code) => {
    try {
      setLoading(true);
      const response = await authService.login2fa(tempToken, code);
      return applySession(response.data);
    } catch (error) {
      const errorMessage = userFacingError(error, 'auth:loginError');
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authService.register(userData);
      
      toast.success(i18n.t('registerSuccess', { ns: 'auth' }));
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = userFacingError(error, 'auth:registerError');
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
      toast.success(i18n.t('logoutSuccess', { ns: 'auth' }));
    }
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token');
      }

      const response = await authService.refreshToken();
      const { access_token } = response.data;
      
      localStorage.setItem('authToken', access_token);
      return access_token;
    } catch (error) {
      // Refresh token inválido, fazer logout
      logout();
      throw error;
    }
  };

  const reloadProfile = async () => {
    const response = await authService.getProfile();
    setUser(response.data);
    return response.data;
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      setUser(response.data);
      toast.success(i18n.t('profileUpdated', { ns: 'auth' }));
      return { success: true, user: response.data };
    } catch (error) {
      const errorMessage = userFacingError(error, 'auth:profileUpdateError');
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    booting,
    loading,
    isAuthenticated,
    login,
    login2fa,
    register,
    logout,
    refreshToken,
    updateProfile,
    reloadProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
